import { Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import {
  getVKByUnitArea,
  rentAttributePriceByVK,
  rentAttributePriceByVKNewBuild,
  RentVK,
  rentVKByAreaThresholds,
} from '../../../common/propertyPriceTypes'
import { useAppSelector } from '../../../redux/hooks'
import {
  priceRenderer,
  unitRentCoverageRenderer,
} from '../../utils/propertyDetailPriceHelpers'
import { useTableLocale } from '../../utils/useTableLocale'
import { PropertyDetailPriceUnitFormValues } from '../PropertyDetailPriceUnitForm/PropertyDetailPriceUnitForm'

interface RentItem {
  key: RentVK
  vkText: string
  refM2: number
  refTotal: number
  medM2: number
  medTotal: number
  minM2: number
  lowTotal: number
  maxM2: number
  upTotal: number
  dataCoverage: number
}

export interface PropertyDetailPriceUnitRentTableProps {
  formValues: PropertyDetailPriceUnitFormValues
}

const noData = (
  <div className="my-2 text-yellow-600">
    Je nám líto, ale pro tuto lokalitu neevidujeme žádná data o nájmech.
  </div>
)

const columns: ColumnsType<RentItem> = [
  {
    title: 'Výměra m²',
    dataIndex: 'vkText',
    width: 90,
  },
  {
    title: 'Ref. cena',
    dataIndex: 'refTotal',
    className: '!text-right',
    render: priceRenderer,
  },
  {
    title: 'Medián',
    dataIndex: 'medTotal',
    className: '!text-right',
    render: priceRenderer,
  },
  {
    title: 'Minimum',
    dataIndex: 'lowTotal',
    className: '!text-right',
    render: priceRenderer,
  },
  {
    title: 'Maximum',
    dataIndex: 'upTotal',
    className: '!text-right',
    render: priceRenderer,
  },
  {
    title: '',
    dataIndex: 'divider',
    className: 'border-r border-solid',
    width: 40,
  },
  {
    title: 'Ref. cena m²',
    dataIndex: 'refM2',
    className: '!text-right',
    render: priceRenderer,
  },
  {
    title: 'Medián m²',
    dataIndex: 'medM2',
    className: '!text-right',
    render: priceRenderer,
  },
  {
    title: 'Minimum m²',
    dataIndex: 'minM2',
    className: '!text-right',
    render: priceRenderer,
  },
  {
    title: 'Maximum m²',
    dataIndex: 'maxM2',
    className: '!text-right',
    render: priceRenderer,
  },
  {
    title: 'Pokrytost',
    dataIndex: 'dataCoverage',
    className: '!text-right',
    render: unitRentCoverageRenderer,
  },
]

const scrollX = { x: 'max-content' }

export const PropertyDetailPriceUnitRentTable: FunctionComponent<PropertyDetailPriceUnitRentTableProps> =
  ({ formValues: unit }) => {
    const unitRent = useAppSelector(
      state => state.property.price.data?.rentReal
    )

    const vkForArea = useMemo<RentVK | undefined>(
      () => (unit?.unitAreaM2 ? getVKByUnitArea(unit.unitAreaM2) : undefined),
      [unit.unitAreaM2]
    )

    const dataSource = useMemo<RentItem[]>(() => {
      const rows: RentItem[] = []
      if (!unitRent || !unitRent.vk?.[1]?.dataCoverage) return rows

      const vks: RentVK[] = [1, 2, 3, 4]
      for (const vk of vks) {
        const rent = unitRent.vk?.[vk]
        if (!rent) continue

        let perM2Delta = 0
        const attrs = unit.isNewBuild
          ? rentAttributePriceByVKNewBuild[vk]
          : rentAttributePriceByVK[vk]

        if (unit.hasBalcony) perM2Delta += attrs.balcony
        if (unit.hasTerrace) perM2Delta += attrs.terrace
        if (unit.hasGarage) perM2Delta += attrs.garage
        if (unit.hasElevator) perM2Delta += attrs.elevator
        if (unit.isNewBuild && unit.isNonPanelOrBrick) {
          perM2Delta += rentAttributePriceByVKNewBuild[vk].otherMaterial
        }

        const medM2 = (rent.median || 0) + perM2Delta
        const minM2 = (rent.lowerInterval || 0) + perM2Delta
        const maxM2 = (rent.upperInterval || 0) + perM2Delta
        const refM2 =
          (unit.isNewBuild
            ? rent.newBuildingAdjustment || 0
            : rent.reference || 0) + perM2Delta

        const areaM2 = unit.unitAreaM2 || 0
        const vkText = rentVKByAreaThresholds[vk - 1].text
        const dataCoverage = rent.dataCoverage || 1

        rows.push({
          key: vk,
          vkText,
          refM2,
          refTotal: refM2 * areaM2,
          medM2,
          medTotal: medM2 * areaM2,
          minM2,
          lowTotal: minM2 * areaM2,
          maxM2,
          upTotal: maxM2 * areaM2,
          dataCoverage,
        })
      }

      return rows
    }, [unit, unitRent])

    const rowClassName = useCallback(
      (record: RentItem) => (record.key === vkForArea ? 'bg-[#e1f0ee]/50' : ''),
      [vkForArea]
    )

    return (
      <div className="space-y-2">
        <Typography.Title level={4} className="!mb-0">
          Tržní nájem
        </Typography.Title>

        <Table
          size="small"
          rowKey="key"
          scroll={scrollX}
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          locale={useTableLocale(!unitRent, 4, noData)}
          rowClassName={rowClassName}
        />
      </div>
    )
  }
